import { AreaDict } from './index';

/**
 * Exit & entry calculator dictionary — Portfolio ▸ Calculator tab
 * (components/PortfolioCalculator.tsx).
 *
 * This screen shipped hardcoded in English, so a German owner who had switched
 * the whole app to Deutsch still met "If you sold this unit today" and "Net
 * proceeds" on the last tab of their portfolio. Keys are prefixed `pcalc`
 * because `pc` is already taken by the portal chrome (pcChangeRole, pcRefresh).
 *
 * NOT translated on purpose: the enquiry payload sent to the Dubai Sell team
 * (name fallback and message body) is data crossing an API to an English-
 * speaking desk, not UI, so it stays English in the component.
 */
export const calculatorDict: AreaDict = {
  en: {
    // ==================== mode switch & toolbar ====================
    pcalcSell: 'Sell',
    pcalcSellSub: 'What your units would return today',
    pcalcBuy: 'Buy',
    pcalcBuySub: 'Discounted units, and the return on reselling',
    pcalcAllDeals: 'All deals',
    pcalcLikeMine: 'Like mine',

    // ==================== cost assumptions ====================
    pcalcCostAssumptions: 'Cost assumptions',
    pcalcAgencyShort: 'agency',
    pcalcProgressionShort: 'progression',
    pcalcSalePriceShort: 'sale price',
    pcalcSaleVsMarket: 'Sale price vs market value',
    pcalcSaleVsMarketNote: 'Test a quicker sale below the current market value, or a strong offer above it.',
    pcalcSalePriceAdj: 'Sale price adjustment',
    pcalcDldFee: 'DLD transfer fee',
    pcalcDldFeeNote: '4% of the transaction, paid to the Dubai Land Department.',
    pcalcAgencyFee: 'Agency fee',
    pcalcAgencyFeeNote: '2.5% of the transaction.',
    pcalcProgressionFee: 'Sales progression fee',
    pcalcProgressionFeeNote: 'Flat AED 4,000 per transaction.',
    pcalcResetCosts: 'Reset to standard costs',
    pcalcDecrease: 'Decrease',
    pcalcIncrease: 'Increase',

    // ==================== sell — unit picker & empty states ====================
    pcalcAddProperty: 'Add a property to calculate what an exit would return.',
    pcalcUnit: 'Unit',
    pcalcNotYetValued: 'not yet valued',
    pcalcThisUnit: 'This unit',
    pcalcCannotValue: 'cannot be valued from Dubai Land Department comparable sales yet, so there is no honest sale price to calculate from. The figures appear here as soon as comparable sales are registered for the building or the community — no price is invented in the meantime.',

    // ==================== sell — headline ====================
    pcalcIfSoldToday: 'If you sold this unit today',
    pcalcOn: 'on',
    pcalcOfCapitalPaidIn: 'of capital paid in',
    pcalcAYear: 'a year',
    pcalcSalePrice: 'Sale price',
    pcalcNetToYou: 'Net to you',

    // ==================== sell — the flow ====================
    pcalcPurchasePrice: 'Purchase price',
    pcalcBought: 'Bought',
    pcalcContractPrice: 'Contract price',
    pcalcMarketToday: 'market value today is',
    pcalcAboveIt: 'above it',
    pcalcBelowIt: 'below it',
    pcalcFromValuationTab: 'Current market value from the Valuation tab',
    pcalcAdjusted: 'adjusted',
    pcalcDldRange: 'DLD range',
    pcalcBalanceOwed: 'Balance owed to the developer',
    pcalcBalanceNote: 'Settled out of the sale before anything reaches you',
    pcalcNetProceeds: 'Net proceeds',
    pcalcCapitalPaidIn: 'Capital you have paid in',
    pcalcPaidInstalments: 'Paid instalments on a purchase price of',
    pcalcNoPlan: 'No payment plan on file — treated as fully paid',
    pcalcGainOnExit: 'Gain on exit',
    pcalcReturnOnCapital: 'Return on capital',
    pcalcPerYearShort: 'pa',

    // ==================== sell — footnote ====================
    pcalcValuedFrom: 'Valued from',
    pcalcDldSaleOne: 'Dubai Land Department sale',
    pcalcDldSaleMany: 'Dubai Land Department sales',
    pcalcConfidence: 'confidence',
    pcalcConfHigh: 'high',
    pcalcConfMedium: 'medium',
    pcalcConfLow: 'low',
    pcalcEstimateNote: 'An estimate, not an offer — a buyer sets the real price.',

    // ==================== buy — states & note ====================
    pcalcSellUnavailable: 'Dubai Sell inventory is unavailable right now. Please try again later.',
    pcalcLoadingInventory: 'Loading Dubai Sell inventory…',
    pcalcSignInToSee: 'Sign in to see the units currently listed on Dubai Sell.',
    pcalcNoMatch: 'No discounted unit currently matches the areas or sizes you already own. Switch to “All deals” to see the rest.',
    pcalcNoDeals: 'No discounted unit is listed with a market reference right now. New stock appears here as it is added to Dubai Sell.',
    pcalcBuyNoteA: 'Units listed on Dubai Sell below their market value. The return shown is what a resale',
    pcalcBuyNoteBold: 'at the market value',
    pcalcBuyNoteB: 'would leave after costs — it assumes you achieve that price, which is the whole risk of the trade.',

    // ==================== buy — card ====================
    pcalcSameArea: 'Same area as a unit you own',
    pcalcSameSize: 'Same size as your',
    pcalcBelowMarket: 'below market',
    pcalcDubaiSellUnit: 'Dubai Sell unit',
    pcalcAskingPrice: 'Asking price',
    pcalcMarketValue: 'Market value',
    pcalcCashToAcquire: 'Cash to acquire',
    pcalcResaleAtMarket: 'Resale at market value',
    pcalcResaleNoteA: 'after the',
    pcalcResaleNoteB: 'agency fee and progression fee on the way out',
    pcalcGainOnResale: 'Gain on resale',
    pcalcHideNumbers: 'Hide the numbers',
    pcalcShowNumbers: 'Show the numbers',
    pcalcEnquiryIntroA: 'This sends your name',
    pcalcEnquiryAnd: 'and',
    pcalcEnquiryIntroB: 'to the Dubai Sell team as an enquiry on this unit. They will contact you directly.',
    pcalcSending: 'Sending…',
    pcalcSendEnquiry: 'Send enquiry',
    pcalcCancel: 'Cancel',
    pcalcRegisterInterest: 'Register interest',
    pcalcEnquirySent: 'Enquiry sent — the Dubai Sell team will be in touch.',
    pcalcCouldNotSend: 'Could not send the enquiry.',
    pcalcReturnIfResold: 'Return if resold at market',
  },

  de: {
    // ==================== mode switch & toolbar ====================
    pcalcSell: 'Verkaufen',
    pcalcSellSub: 'Was Ihre Einheiten heute einbringen würden',
    pcalcBuy: 'Kaufen',
    pcalcBuySub: 'Vergünstigte Einheiten und die Rendite beim Weiterverkauf',
    pcalcAllDeals: 'Alle Angebote',
    pcalcLikeMine: 'Wie meine',

    // ==================== cost assumptions ====================
    pcalcCostAssumptions: 'Kostenannahmen',
    pcalcAgencyShort: 'Provision',
    pcalcProgressionShort: 'Abwicklung',
    pcalcSalePriceShort: 'Verkaufspreis',
    pcalcSaleVsMarket: 'Verkaufspreis gegenüber Marktwert',
    pcalcSaleVsMarketNote: 'Testen Sie einen schnelleren Verkauf unter dem aktuellen Marktwert — oder ein starkes Angebot darüber.',
    pcalcSalePriceAdj: 'Anpassung des Verkaufspreises',
    pcalcDldFee: 'DLD-Übertragungsgebühr',
    pcalcDldFeeNote: '4 % der Transaktion, zahlbar an das Dubai Land Department.',
    pcalcAgencyFee: 'Maklerprovision',
    pcalcAgencyFeeNote: '2,5 % der Transaktion.',
    pcalcProgressionFee: 'Abwicklungsgebühr',
    pcalcProgressionFeeNote: 'Pauschal AED 4.000 pro Transaktion.',
    pcalcResetCosts: 'Auf Standardkosten zurücksetzen',
    pcalcDecrease: 'Verringern',
    pcalcIncrease: 'Erhöhen',

    // ==================== sell — unit picker & empty states ====================
    pcalcAddProperty: 'Fügen Sie eine Immobilie hinzu, um zu berechnen, was ein Verkauf einbringen würde.',
    pcalcUnit: 'Einheit',
    pcalcNotYetValued: 'noch nicht bewertet',
    pcalcThisUnit: 'Diese Einheit',
    pcalcCannotValue: 'kann noch nicht aus Vergleichsverkäufen des Dubai Land Department bewertet werden — es gibt also keinen belastbaren Verkaufspreis als Grundlage. Die Zahlen erscheinen hier, sobald Vergleichsverkäufe für das Gebäude oder die Community registriert sind. Bis dahin wird kein Preis erfunden.',

    // ==================== sell — headline ====================
    pcalcIfSoldToday: 'Wenn Sie diese Einheit heute verkaufen würden',
    pcalcOn: 'auf',
    pcalcOfCapitalPaidIn: 'eingezahltes Kapital',
    pcalcAYear: 'pro Jahr',
    pcalcSalePrice: 'Verkaufspreis',
    pcalcNetToYou: 'Netto für Sie',

    // ==================== sell — the flow ====================
    pcalcPurchasePrice: 'Kaufpreis',
    pcalcBought: 'Gekauft am',
    pcalcContractPrice: 'Vertragspreis',
    pcalcMarketToday: 'Marktwert heute liegt',
    pcalcAboveIt: 'darüber',
    pcalcBelowIt: 'darunter',
    pcalcFromValuationTab: 'Aktueller Marktwert aus dem Tab „Bewertung“',
    pcalcAdjusted: 'angepasst',
    pcalcDldRange: 'DLD-Spanne',
    pcalcBalanceOwed: 'Restschuld gegenüber dem Bauträger',
    pcalcBalanceNote: 'Wird aus dem Verkauf beglichen, bevor etwas bei Ihnen ankommt',
    pcalcNetProceeds: 'Nettoerlös',
    pcalcCapitalPaidIn: 'Von Ihnen eingezahltes Kapital',
    pcalcPaidInstalments: 'Bezahlte Raten auf einen Kaufpreis von',
    pcalcNoPlan: 'Kein Zahlungsplan hinterlegt — als vollständig bezahlt behandelt',
    pcalcGainOnExit: 'Gewinn beim Verkauf',
    pcalcReturnOnCapital: 'Rendite auf das Kapital',
    pcalcPerYearShort: 'p. a.',

    // ==================== sell — footnote ====================
    pcalcValuedFrom: 'Bewertet aus',
    pcalcDldSaleOne: 'Verkauf des Dubai Land Department',
    pcalcDldSaleMany: 'Verkäufen des Dubai Land Department',
    pcalcConfidence: 'Konfidenz',
    pcalcConfHigh: 'hohe',
    pcalcConfMedium: 'mittlere',
    pcalcConfLow: 'niedrige',
    pcalcEstimateNote: 'Eine Schätzung, kein Angebot — den tatsächlichen Preis bestimmt der Käufer.',

    // ==================== buy — states & note ====================
    pcalcSellUnavailable: 'Der Bestand von Dubai Sell ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.',
    pcalcLoadingInventory: 'Dubai-Sell-Bestand wird geladen…',
    pcalcSignInToSee: 'Melden Sie sich an, um die aktuell bei Dubai Sell gelisteten Einheiten zu sehen.',
    pcalcNoMatch: 'Derzeit passt keine vergünstigte Einheit zu den Lagen oder Größen, die Sie bereits besitzen. Wechseln Sie zu „Alle Angebote“, um die übrigen zu sehen.',
    pcalcNoDeals: 'Derzeit ist keine vergünstigte Einheit mit Marktreferenz gelistet. Neue Objekte erscheinen hier, sobald sie bei Dubai Sell aufgenommen werden.',
    pcalcBuyNoteA: 'Bei Dubai Sell unter Marktwert gelistete Einheiten. Die gezeigte Rendite ist das, was ein Weiterverkauf',
    pcalcBuyNoteBold: 'zum Marktwert',
    pcalcBuyNoteB: 'nach Kosten übrig lässt — vorausgesetzt, Sie erzielen diesen Preis. Genau darin liegt das Risiko.',

    // ==================== buy — card ====================
    pcalcSameArea: 'Gleiche Lage wie eine Ihrer Einheiten',
    pcalcSameSize: 'Gleiche Größe wie Ihre',
    pcalcBelowMarket: 'unter Marktwert',
    pcalcDubaiSellUnit: 'Dubai-Sell-Einheit',
    pcalcAskingPrice: 'Angebotspreis',
    pcalcMarketValue: 'Marktwert',
    pcalcCashToAcquire: 'Kapitaleinsatz beim Kauf',
    pcalcResaleAtMarket: 'Weiterverkauf zum Marktwert',
    pcalcResaleNoteA: 'nach der',
    pcalcResaleNoteB: 'Maklerprovision und der Abwicklungsgebühr beim Verkauf',
    pcalcGainOnResale: 'Gewinn beim Weiterverkauf',
    pcalcHideNumbers: 'Zahlen ausblenden',
    pcalcShowNumbers: 'Zahlen anzeigen',
    pcalcEnquiryIntroA: 'Damit senden wir Ihren Namen',
    pcalcEnquiryAnd: 'und',
    pcalcEnquiryIntroB: 'als Anfrage zu dieser Einheit an das Dubai-Sell-Team. Das Team meldet sich direkt bei Ihnen.',
    pcalcSending: 'Wird gesendet…',
    pcalcSendEnquiry: 'Anfrage senden',
    pcalcCancel: 'Abbrechen',
    pcalcRegisterInterest: 'Interesse bekunden',
    pcalcEnquirySent: 'Anfrage gesendet — das Dubai-Sell-Team meldet sich bei Ihnen.',
    pcalcCouldNotSend: 'Die Anfrage konnte nicht gesendet werden.',
    pcalcReturnIfResold: 'Rendite bei Weiterverkauf zum Marktwert',
  },
};
